// Notification centre in the app (T-244, US-125): the bell in the top bar with the unread
// count and the latest notifications, the Notifications card of My account (cadence per type,
// demos with their own cadence, interest alerts), the per-demo setting on the demo page and
// the Follow control on the asset page. Texts come from the notify.* messages; the values
// (person, demo, asset, chapter, days, question) are stored with each notification.

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

import { api, problemText } from "../api";
import type { components } from "../api-schema";
import { formatDate, t, type MessageKey } from "../i18n";
import { Icon } from "./icons";
import { useToast } from "./overlay";
import { Button, Card } from "./ui";

type Schemas = components["schemas"];
export type Notification = Schemas["Notification"];
type NotificationPage = Schemas["NotificationPage"];
type Settings = Schemas["NotificationSettings"];

const CADENCES = ["realtime", "daily", "weekly", "app", "off"] as const;

/// The sentence of one notification in the reader's language.
export function notificationText(n: Pick<Notification, "kind" | "args">): string {
  const args = (n.args ?? {}) as Record<string, string | number>;
  const person = typeof args.person === "string" && args.person !== "" ? args.person : t("notify.someone");
  return t(`notify.${n.kind}` as MessageKey, { ...args, person });
}

export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The badge refreshes every 30 seconds; the list loads when the menu opens.
  const unread = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => api.get<{ unread: number }>("/v1/notifications/unread"),
    refetchInterval: 30_000,
  });
  const list = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => api.get<NotificationPage>("/v1/notifications?limit=20"),
    enabled: false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["notifications"] });
  const read = useMutation({
    mutationFn: (id: number | null) => api.post(id === null ? "/v1/notifications/read" : `/v1/notifications/${id}/read`),
    onSuccess: refresh,
  });
  const count = unread.data?.unread ?? 0;
  const items = list.data?.items ?? [];

  return (
    <DropdownMenu.Root onOpenChange={(open) => open && void list.refetch()}>
      <DropdownMenu.Trigger
        aria-label={count > 0 ? t("notify.bellUnread", { count }) : t("notify.bell")}
        className="relative grid h-8 w-8 place-items-center rounded-full border-0 bg-transparent text-steel hover:bg-panel hover:text-ink"
      >
        <Icon name="bell" size={18} />
        {count > 0 && (
          <span className="num absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-blue px-1 text-center text-[10px] font-semibold leading-4 text-on-blue">{count > 99 ? "99+" : count}</span>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className="z-50 w-[min(360px,calc(100vw-32px))] rounded-[10px] border border-rule bg-ground p-1.5 shadow-lg">
          <div className="flex items-center justify-between px-2.5 py-1.5">
            <span className="text-sm font-medium">{t("notify.title")}</span>
            {count > 0 && (
              <button type="button" className="border-0 bg-transparent p-0 text-xs text-blue hover:underline" onClick={() => read.mutate(null)}>
                {t("notify.markAll")}
              </button>
            )}
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-rule-soft" />
          {list.isFetching && items.length === 0 && <p className="m-0 px-2.5 py-3 text-sm text-steel">{t("common.loading")}</p>}
          {!list.isFetching && items.length === 0 && <p className="m-0 px-2.5 py-3 text-sm text-steel">{t("notify.empty")}</p>}
          <div className="max-h-[60vh] overflow-y-auto">
            {items.map((n) => (
              <DropdownMenu.Item
                key={n.id}
                onSelect={() => {
                  if (!n.read_at) read.mutate(n.id);
                  navigate(n.path);
                }}
                className="flex cursor-pointer gap-2.5 rounded-[6px] px-2.5 py-2 outline-none data-[highlighted]:bg-panel"
              >
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read_at ? "bg-transparent" : "bg-blue"}`} aria-hidden />
                <span className="flex min-w-0 flex-col">
                  <span className={`text-sm ${n.read_at ? "text-steel" : ""}`}>{notificationText(n)}</span>
                  <span className="text-xs text-steel">{formatDate(n.created_at, { dateStyle: "medium", timeStyle: "short" })}</span>
                </span>
              </DropdownMenu.Item>
            ))}
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-rule-soft" />
          <DropdownMenu.Item onSelect={() => navigate("/account#notifications")} className="cursor-pointer rounded-[6px] px-2.5 py-1.5 text-xs text-steel outline-none data-[highlighted]:bg-panel">
            {t("notify.settings")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function useSettings() {
  return useQuery({ queryKey: ["notification-settings"], queryFn: () => api.get<Settings>("/v1/me/notification-settings") });
}

function useSettingsChange<T>(fn: (value: T) => Promise<unknown>) {
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notification-settings"] }),
    onError: (error) => toast(problemText(error), "error"),
  });
}

function CadenceSelect({ value, allowed, label, onChange, inherit }: { value: string; allowed: readonly string[]; label: string; onChange: (v: string) => void; inherit?: boolean }) {
  return (
    <select className="rounded-[7px] border border-rule bg-ground px-2 py-1.5 text-sm" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {inherit && <option value="">{t("notify.cadence.inherit")}</option>}
      {allowed.map((c) => (
        <option key={c} value={c}>
          {t(`notify.cadence.${c}` as MessageKey)}
        </option>
      ))}
    </select>
  );
}

/// The Notifications card of My account.
export function NotificationSettingsCard() {
  const settings = useSettings();
  const setType = useSettingsChange(({ kind, cadence }: { kind: string; cadence: string }) => api.put(`/v1/me/notification-settings/types/${kind}`, { cadence }));
  const clearDemo = useSettingsChange((id: string) => api.del(`/v1/me/notification-settings/demos/${id}`));
  const unfollow = useSettingsChange((id: string) => api.del(`/v1/me/interests/${id}`));
  const s = settings.data;
  // Opened from the bell as /account#notifications: bring the card into view once loaded.
  const location = useLocation();
  useEffect(() => {
    if (s && location.hash === "#notifications") document.getElementById("notifications")?.scrollIntoView({ block: "start" });
  }, [s, location.hash]);
  return (
    <div id="notifications" className="scroll-mt-4 lg:col-span-2">
      <Card title={t("notify.title")}>
        <div className="grid gap-4">
          <p className="m-0 text-sm text-steel">{t("notify.settingsHint")}</p>
          {settings.isError && <p className="m-0 text-err">{problemText(settings.error)}</p>}
          {s && (
            <div className="grid gap-2">
              {s.types.map((type) => (
                <div key={type.kind} className="flex flex-wrap items-center justify-between gap-2 border-b border-rule-soft pb-2 last:border-b-0">
                  <span className="flex flex-col">
                    <span className="text-sm font-medium">{t(`notify.type.${type.kind}` as MessageKey)}</span>
                    <span className="text-xs text-steel">{t(`notify.type.${type.kind}.hint` as MessageKey)}</span>
                  </span>
                  <CadenceSelect value={type.cadence} allowed={type.allowed} label={t(`notify.type.${type.kind}` as MessageKey)} onChange={(cadence) => setType.mutate({ kind: type.kind, cadence })} />
                </div>
              ))}
            </div>
          )}
          {s && s.demos.length > 0 && (
            <div>
              <h3 className="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-steel">{t("notify.demos")}</h3>
              <ul className="m-0 grid list-none gap-1.5 p-0">
                {s.demos.map((d) => (
                  <li key={d.demo_id} className="flex items-center justify-between gap-2 text-sm">
                    <span>
                      {d.name} <span className="text-steel">· {t(`notify.cadence.${d.cadence}` as MessageKey)}</span>
                    </span>
                    <Button size="sm" kind="ghost" onClick={() => clearDemo.mutate(d.demo_id)}>
                      {t("notify.reset")}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s && (
            <div>
              <h3 className="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-steel">{t("notify.interests")}</h3>
              {s.interests.length === 0 ? (
                <p className="m-0 text-sm text-steel">{t("notify.interestsEmpty")}</p>
              ) : (
                <ul className="m-0 grid list-none gap-1.5 p-0">
                  {s.interests.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-2 text-sm">
                      <span>
                        {r.asset_title} <span className="text-steel">· {r.chapter == null ? t("notify.anyChapter") : t("notify.chapterNumber", { number: r.chapter + 1 })}</span>
                      </span>
                      <Button size="sm" kind="ghost" onClick={() => unfollow.mutate(r.id)}>
                        {t("notify.unfollow")}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

/// The caller's own cadence for one demo, on the demo page.
export function DemoNotificationSetting({ demoId }: { demoId: string }) {
  const settings = useSettings();
  const set = useSettingsChange((cadence: string) => (cadence === "" ? api.del(`/v1/me/notification-settings/demos/${demoId}`) : api.put(`/v1/me/notification-settings/demos/${demoId}`, { cadence })));
  const current = settings.data?.demos.find((d) => d.demo_id === demoId)?.cadence ?? "";
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-xs text-steel">{t("notify.forDemo")}</span>
      <CadenceSelect value={current} allowed={CADENCES} label={t("notify.forDemo")} inherit onChange={(v) => set.mutate(v)} />
    </label>
  );
}

/// Follow an asset (any chapter) or one chapter of it for interest alerts.
export function FollowButton({ assetId, chapter, compact }: { assetId: string; chapter?: number; compact?: boolean }) {
  const settings = useSettings();
  const toast = useToast();
  const rule = settings.data?.interests.find((r) => r.asset_id === assetId && (r.chapter ?? undefined) === chapter);
  const follow = useSettingsChange(() => api.post("/v1/me/interests", { asset_id: assetId, chapter: chapter ?? null }));
  const unfollow = useSettingsChange((id: string) => api.del(`/v1/me/interests/${id}`));
  const label = rule ? t("notify.unfollow") : chapter === undefined ? t("notify.follow") : t("notify.followChapter");
  const toggle = () => (rule ? unfollow.mutate(rule.id) : follow.mutate(undefined, { onSuccess: () => toast(chapter === undefined ? t("notify.followed") : t("notify.followedChapter"), "success") }));
  if (compact) {
    // An icon toggle for chapter rows; the bell is filled in blue while followed.
    return (
      <button
        type="button"
        aria-label={label}
        aria-pressed={!!rule}
        title={t("notify.followChapterHint")}
        disabled={!settings.data || follow.isPending || unfollow.isPending}
        onClick={toggle}
        className={`grid h-6 w-6 shrink-0 place-items-center rounded-[5px] border-0 bg-transparent p-0 hover:bg-panel ${rule ? "text-blue" : "text-steel"}`}
      >
        <Icon name="bell" size={14} />
      </button>
    );
  }
  return (
    <Button size="sm" kind="ghost" aria-pressed={!!rule} title={t("notify.followHint")} disabled={!settings.data || follow.isPending || unfollow.isPending} onClick={toggle}>
      <Icon name="bell" size={14} />
      {label}
    </Button>
  );
}
